import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, ModalContent, ModalCloseButton } from '../common';
import PlayerSearchInput from '../common/PlayerSearchInput';
import PartnershipConflictModal from './PartnershipConflictModal';
import useSessionPartnershipCreate from '../../hooks/useSessionPartnershipCreate';

/**
 * Modal to add a session partnership (same rules as Manage → Partnerships).
 */
export default function AddSessionPartnershipModal({
  isOpen,
  onClose,
  players = [],
  partnerships = [],
  onUpdatePartnerships,
  sessionPlayerStatus = {},
  sessionId = null,
  playersById = {},
  onToast,
  loadSessionPartnerships,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
}) {
  const [selectedPlayer1, setSelectedPlayer1] = useState(null);
  const [selectedPlayer2, setSelectedPlayer2] = useState(null);
  const [error, setError] = useState('');

  const resolvedPlayersById = useMemo(() => {
    const map = { ...playersById };
    players.forEach((p) => {
      if (p?.id) map[p.id] = { ...map[p.id], ...p };
    });
    return map;
  }, [playersById, players]);

  const { isLoading, createPartnership, conflictModalProps } = useSessionPartnershipCreate({
    sessionId,
    partnerships,
    onUpdatePartnerships,
    playersById: resolvedPlayersById,
    onToast,
    loadSessionPartnerships,
    offlineQueue,
    session,
    getSnapshot,
  });

  useEffect(() => {
    if (!isOpen) return;
    setSelectedPlayer1(null);
    setSelectedPlayer2(null);
    setError('');
  }, [isOpen]);

  const availablePlayers = useMemo(() => {
    const partneredPlayerIds = new Set(partnerships.flatMap((p) => [p.player1Id, p.player2Id]));
    return players.filter(
      (player) =>
        player?.id &&
        !partneredPlayerIds.has(player.id) &&
        sessionPlayerStatus[player.id] === 'active'
    );
  }, [players, partnerships, sessionPlayerStatus]);

  const transformedAvailablePlayers = useMemo(
    () =>
      availablePlayers.map((player) => {
        if (player.first_name && player.last_name) return player;
        const nameParts = (player.name || '').trim().split(' ');
        return {
          ...player,
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' ') || '',
        };
      }),
    [availablePlayers]
  );

  const getAvailablePlayersForPlayer1 = () => {
    if (!selectedPlayer2) return transformedAvailablePlayers;
    return transformedAvailablePlayers.filter((p) => p.id !== selectedPlayer2.id);
  };

  const getAvailablePlayersForPlayer2 = () => {
    if (!selectedPlayer1) return transformedAvailablePlayers;
    return transformedAvailablePlayers.filter((p) => p.id !== selectedPlayer1.id);
  };

  const hasMinimumAvailablePlayers = availablePlayers.length >= 2;
  // Keep the form visible while submitting; only show the warning when idle and
  // there truly aren't enough unpartnered active players.
  const showInsufficientPlayersWarning = !hasMinimumAvailablePlayers && !isLoading;
  const showPartnershipForm = hasMinimumAvailablePlayers || isLoading;

  const handleAddPartnership = async () => {
    if (!selectedPlayer1 || !selectedPlayer2) {
      setError('Please select both players');
      return;
    }
    if (selectedPlayer1.id === selectedPlayer2.id) {
      setError('Cannot partner a player with themselves');
      return;
    }
    if (!sessionId) {
      setError('Session ID is required to create partnerships');
      return;
    }
    const existingPartnership = partnerships.find(
      (p) =>
        p.player1Id === selectedPlayer1.id ||
        p.player1Id === selectedPlayer2.id ||
        p.player2Id === selectedPlayer1.id ||
        p.player2Id === selectedPlayer2.id
    );
    if (existingPartnership) {
      setError('One or both players are already in a partnership');
      return;
    }

    setError('');
    const result = await createPartnership(selectedPlayer1.id, selectedPlayer2.id, {
      onSuccess: () => {
        onClose();
        setSelectedPlayer1(null);
        setSelectedPlayer2(null);
      },
    });

    if (!result.success && result.code !== 'PLAYER_ALREADY_PARTNERED' && result.error) {
      setError(result.error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent size="large" className="max-h-[90vh] overflow-y-auto">
          <ModalCloseButton onClose={onClose} />
          <h2 className="pr-8 text-lg font-semibold text-text-primary">Add partnership</h2>
          <p className="mt-2 text-sm text-text-secondary">
            Both players must be marked <span className="font-medium">playing</span> in this session. They will
            always be placed on the same team when matchmaking runs.
          </p>

          {showInsufficientPlayersWarning && (
            <p className="mt-4 rounded-md border border-warning/30 bg-warning-light/40 p-3 text-sm text-warning-dark">
              You need at least two active players who are not already in a partnership. Add players to the session
              and mark them as playing first, or remove an existing partnership.
            </p>
          )}

          {showPartnershipForm && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="min-w-0 flex-1">
                  <label className="mb-1 block text-xs text-text-muted">Player 1</label>
                  <PlayerSearchInput
                    placeholder="Search for player 1…"
                    availablePlayers={getAvailablePlayersForPlayer1()}
                    selectedPlayer={selectedPlayer1}
                    onSelectPlayer={(player) => setSelectedPlayer1(player)}
                    onRemovePlayer={() => setSelectedPlayer1(null)}
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <label className="mb-1 block text-xs text-text-muted">Player 2</label>
                  <PlayerSearchInput
                    placeholder="Search for player 2…"
                    availablePlayers={getAvailablePlayersForPlayer2()}
                    selectedPlayer={selectedPlayer2}
                    onSelectPlayer={(player) => setSelectedPlayer2(player)}
                    onRemovePlayer={() => setSelectedPlayer2(null)}
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>
              </div>
              {error ? <div className="text-sm text-danger-dark">{error}</div> : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="small" onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="small"
                  onClick={handleAddPartnership}
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating…' : 'Create partnership'}
                </Button>
              </div>
            </div>
          )}

          {showInsufficientPlayersWarning && (
            <div className="mt-6 flex justify-end">
              <Button type="button" variant="outline" size="small" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </ModalContent>
      </Modal>
      <PartnershipConflictModal {...conflictModalProps} />
    </>
  );
}
